<template>
    <div
        class="rounded-lg pt-2 h-full"
        :class="{ 'flex items-center justify-center': loading || !chartOptions || !chartOptions.series }"
    >
        <no-data-animation v-if="loading" />
        <template v-else-if="chartOptions && Object.keys(chartOptions).length">
            <highcharts
                ref="xaxis-chart"
                class="chart-content_wrapper"
                :options="chartOptions"
                :callback="onInitChartCallback"
                :updateArgs="[true, true]"
            />
        </template>
        <template v-else>
            <div>
                <icon-no-data-cry class="w-64" />
                <h3 class="text-main-xl text-center">{{ $t('general.noData') }}</h3>
            </div>
        </template>
    </div>
</template>
<script>
    import get from 'lodash/get'
    import { Chart } from 'highcharts-vue'
    import Gantt from 'highcharts/modules/gantt'
    import { getDefaultTimeDelay } from '@/enum/generic'
    import actionMixin from '@/components/Charts/Configs/actionMixin'
    import { colorsForPieChartCallsPerAccColorsLayout } from '@/enum/colors'
    import uniq from 'lodash/uniq'
    import highchartsMoreInit from 'highcharts/highcharts-more'
    import Highcharts from './Configs/Xrange'
    import requestMixin from "@/mixins/requestMixin";

    highchartsMoreInit(Highcharts)
    Gantt(Highcharts)

    export default {
        mixins: [actionMixin, requestMixin],
        components: {
            Highcharts,
            highcharts: Chart,
            NoDataAnimation: () => import('@/components/NoDataAnimation.vue')
        },
        props: {
            data: {
                type: Object,
                default: () => ({})
            },
            editable: {
                type: Boolean,
                default: false
            },
        },
        computed: {
            getAccountStatuses () {
                return this.$store.getters['entities/accountStatuses']
            }
        },
        data() {
            return {
                chartVisibility: true,
                fetchDataInterval: null,
                chartOptions: {},
                chartInstance: false,
                loading: true
            }
        },
        methods: {
            onInitChartCallback(chart) {
                this.chartInstance = chart
            },
            tryPrintChart() {
                const divToPrint = this.$el.children[0]
                const newWin = window.open();
                newWin.document.write(divToPrint.innerHTML);
                newWin.document.close();
                newWin.focus();
                newWin.print();
            },
            tryDownloadChart(type) {
                this.$refs['xaxis-chart'].chart.exportChart({
                    type: type,
                })
            },
            async getWidgetData() {
                this.loading = true
                try {
                    let Data = await this.loadWidgetData(this.data)
                    let chartData = get(Data, '0')
                    if (!chartData.GanttData || !chartData.GanttData.length) {
                        return
                    }

                    chartData.series = []

                    const transformChartData = await this.divideDataByStatus(chartData)

                    this.chartOptions = {
                        yAxis: {
                            ...{
                                title: {
                                    text: '',
                                },
                                reversed: true,
                            },
                            categories: transformChartData.categories
                        },
                        series: transformChartData.series,
                        chart: {
                            type: 'gantt',
                            height: this.$el.clientHeight - 20,
                            backgroundColor: 'var(--colorWidgetBackground-color)'
                        },
                        title: {
                            text: ''
                        },
                        credits: {
                            enabled: false
                        },
                        tooltip: {
                            useHTML: true,
                            formatter: function () {
                                const transformDate = (data) => {
                                    const dateTimeArray = new Date(data).toISOString().split('T')
                                    const time = dateTimeArray[1].substring(0, 8)

                                    return `${time}`
                                }

                                return `<span class="main-text-color">${this.point.lable} (${this.point.cdr_type}) (${transformDate(this.point.x)} - ${transformDate(this.point.x2)})</span>`
                            },
                            backgroundColor: 'var(--mainBgColor-color)',
                            borderColor: '#ffffff',
                            boxShadow: '0 10px 15px 0 rgba(143, 149, 163, 0.38)',
                            borderRadius: 10,
                            color: 'var(--mainTextColor-color)'
                        },
                        time: {
                            useUTC: true
                        },
                        xAxis: {
                            type: 'datetime'
                        }
                    }

                    this.chartVisibility = false
                    this.$nextTick(() => {
                        this.chartVisibility = true
                    })
                } catch (e) {
                    console.warn(e)
                    let status = get(e, 'response.status')
                    if (status === 400) {
                        let refreshDelay = getDefaultTimeDelay()
                        this.$set(this.data, 'DefaultRefreshInterval', refreshDelay)
                    }
                } finally {
                    setTimeout(() => {
                        this.loading = false
                    }, 500)
                }
            },
            getWidgetDataWithRefreshInterval () {
                if (this.fetchDataInterval) {
                    clearInterval(this.fetchDataInterval)
                }
                if (this.data.DefaultRefreshInterval) {
                    this.fetchDataInterval = setInterval(async() => {
                        await this.getWidgetData()
                    }, this.data.DefaultRefreshInterval)
                }
            },
            async divideDataByStatus (chartData) {
                const GanttDataGroupedByStatus = {}
                uniq(chartData.GanttData
                    .map(el => el.cdr_type))
                    .forEach(el => {

                        GanttDataGroupedByStatus[el] = []
                    })

                chartData.GanttData.forEach(el => {
                    GanttDataGroupedByStatus[el.cdr_type].push(el)
                })

                for (const key in GanttDataGroupedByStatus) {
                    chartData.series.push({
                        name: key,
                        pointWidth: 15,
                        pointPadding: 20,
                        data: GanttDataGroupedByStatus[key]
                    })
                }

                chartData.series = chartData.series
                    .map(el => {
                        el.data = el.data
                            .map(value => {
                                value.x = new Date(value.x).getTime()
                                value.x2 = new Date(value.x2).getTime()

                                const status = value.cdr_type
                                const statusByAccount = this.getAccountStatuses.find(account => account.Name === status)

                                if (status.toLowerCase().includes('outgoing')) {
                                    value.color = colorsForPieChartCallsPerAccColorsLayout.Outgoing
                                } else if (status.toLowerCase().includes('incoming')) {
                                    value.color = colorsForPieChartCallsPerAccColorsLayout.Incoming
                                } else if (statusByAccount && statusByAccount.ColorCode) {
                                    value.color = statusByAccount.ColorCode
                                } else {
                                    value.color = colorsForPieChartCallsPerAccColorsLayout.Outgoing
                                }

                                return value
                            })

                        return el
                    })

                return chartData
            }
        },
        mounted() {
            this.getWidgetData()
        },
        watch: {
            data: {
                immediate: true,
                handler: function () {
                    this.getWidgetData()
                    this.getWidgetDataWithRefreshInterval()
                }
            },
        },
        beforeDestroy() {
            if (this.fetchDataInterval) {
                clearInterval(this.fetchDataInterval)
            }
        },
    }
</script>
<style lang="scss" scoped>
::v-deep .highcharts-point {
    stroke: none !important;
}
::v-deep .highcharts-legend-item-hidden {
    opacity: 0.2 !important;
}
::v-deep .highcharts-credits {
    display: none !important;
}
::v-deep .highcharts-xaxis-labels text {
    color: var(--mainTextColor-color) !important;
    fill: var(--mainTextColor-color) !important;
}
</style>

<style>
.animation-block {
    height: 350px;
    width: 350px;
    margin: 0 auto;
    text-align: center;
}
</style>
